import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus, Trash, Gear } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const useList = (entity) => useQuery({ queryKey: ["entity", entity], queryFn: async () => (await api.get(`/${entity}`)).data });

export const derivedFromPlan = (plan) => ({
  assigned_machines: [...new Set((plan || []).map((r) => r.machine_id).filter(Boolean))],
  assigned_operators: [...new Set((plan || []).flatMap((r) => r.operator_ids || []))],
});

export default function MachinePlanEditor({ value, onChange, testIdPrefix = "mplan" }) {
  const machines = useList("machines");
  const team = useList("team");
  const rows = value || [];

  const update = (i, patch) => onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRow = () => onChange([...rows, { machine_id: "", planned_hours: "", operator_ids: [] }]);
  const removeRow = (i) => onChange(rows.filter((_, idx) => idx !== i));

  const toggleOperator = (i, id) => {
    const cur = rows[i].operator_ids || [];
    update(i, { operator_ids: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] });
  };

  const used = rows.map((r) => r.machine_id).filter(Boolean);

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-editor`}>
      {rows.length === 0 && (
        <div className="rounded-md border border-dashed border-slate-300 p-4 text-sm text-slate-400 text-center">
          No machines allocated yet.
        </div>
      )}

      {rows.map((row, i) => {
        const operators = team.data || [];
        const picked = row.operator_ids || [];
        return (
          <div key={i} className="rounded-lg border border-slate-200 p-3 space-y-3" data-testid={`${testIdPrefix}-row-${i}`}>
            <div className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <label className="text-xs font-medium text-slate-500">Machine</label>
                <Select value={row.machine_id || ""} onValueChange={(v) => update(i, { machine_id: v })}>
                  <SelectTrigger className="mt-1" data-testid={`${testIdPrefix}-machine-${i}`}>
                    <SelectValue placeholder="Select machine..." />
                  </SelectTrigger>
                  <SelectContent>
                    {(machines.data || []).map((m) => (
                      <SelectItem key={m.id} value={m.id} disabled={used.includes(m.id) && row.machine_id !== m.id}>
                        {m.code} · {m.machine_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-28 shrink-0">
                <label className="text-xs font-medium text-slate-500">Planned hrs</label>
                <Input type="number" inputMode="decimal" min="0" className="mt-1"
                  value={row.planned_hours ?? ""} onChange={(e) => update(i, { planned_hours: e.target.value })}
                  placeholder="0" data-testid={`${testIdPrefix}-hours-${i}`} />
              </div>
              <button type="button" onClick={() => removeRow(i)}
                className="mt-6 p-2 text-slate-400 hover:text-red-600 transition-colors"
                title="Remove machine" data-testid={`${testIdPrefix}-remove-${i}`}>
                <Trash className="h-4 w-4" />
              </button>
            </div>

            <div>
              <label className="text-xs font-medium text-slate-500">
                Operators on this machine {picked.length > 0 && <span className="text-slate-400">({picked.length} selected)</span>}
              </label>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {operators.length === 0 ? (
                  <span className="text-xs text-slate-400">No team members yet</span>
                ) : operators.map((t) => (
                  <button key={t.id} type="button" onClick={() => toggleOperator(i, t.id)}
                    className={`px-2.5 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                      picked.includes(t.id) ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                    }`}
                    data-testid={`${testIdPrefix}-op-${i}-${t.code}`}>
                    {t.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );
      })}

      <Button type="button" variant="outline" size="sm" onClick={addRow} data-testid={`${testIdPrefix}-add-row`}>
        <Plus className="h-4 w-4 mr-1.5" weight="bold" /> Add machine
      </Button>

      {rows.length > 0 && (
        <div className="flex items-center gap-1.5 text-xs text-slate-500">
          <Gear className="h-3.5 w-3.5" />
          Total planned {rows.reduce((s, r) => s + (Number(r.planned_hours) || 0), 0)} h across {rows.length} machine{rows.length === 1 ? "" : "s"}
        </div>
      )}
    </div>
  );
}
